import { StrictMode, type ComponentProps } from "react";
import { createRoot } from "react-dom/client";
import { ConvexBetterAuthProvider } from "@convex-dev/better-auth/react";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageSync } from "@/i18n/LanguageSync";
import { authClient } from "@/lib/auth-client";
import { convex } from "@/lib/convex";
import { routeTree } from "./routeTree.gen";
import "@/i18n";
import "./index.css";

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
});

// TS 6.0 fails to relate the client's inferred plugin types to the provider's
// `AuthClient` (it resolves the session type to `never`). The provider only
// calls methods every Better Auth client has, so the cast changes nothing at
// runtime; casting the plugin instead, as Joby does, would erase the
// organization methods this app calls.
type ProviderAuthClient = ComponentProps<typeof ConvexBetterAuthProvider>["authClient"];

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    {/* Dark unless told otherwise. index.html applies the same rule before
        first paint, so there is no flash of the wrong theme. */}
    <ThemeProvider
      attribute="class"
      defaultTheme="dark"
      enableSystem
      disableTransitionOnChange
      // next-themes renders its own pre-paint script, which a client-rendered
      // app can never run (index.html already did that job). Typing it as
      // data keeps React from reporting a script it will not execute.
      scriptProps={{ type: "application/json" }}
    >
      <ConvexBetterAuthProvider
        client={convex}
        authClient={authClient as unknown as ProviderAuthClient}
      >
        <TooltipProvider>
          <LanguageSync />
          <RouterProvider router={router} />
          <Toaster />
        </TooltipProvider>
      </ConvexBetterAuthProvider>
    </ThemeProvider>
  </StrictMode>,
);
